import React, { useEffect } from "react";
import { useBoolean } from "react-hanger";
import { connect } from "react-redux";
import SetInterval from "set-interval";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorIcon from "@mui/icons-material/Error";

import { Emphasize } from "../../../../styles";
import { csActions } from "../../../../../redux/actions";

const getDropzoneBackgroundColor = (props) => {
  if (props.isDragActive)
    return "#fafafa";

  return "#ebebeb";
}

const DropzoneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 1px;
  border-radius: 4px;
  border-style: dashed;
  border-color: #aaaaaa;
  background-color: ${props => getDropzoneBackgroundColor(props)};
  color: #8a8a8a;
  outline: none;
`;

const StyledDropzone = (props) => {
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: "application/pdf",
    maxFiles: 1,
    multiple: false,
  });

  useEffect(() => {
    if (acceptedFiles.length > 0)
      props.onFileAccepted(acceptedFiles[0]);
  }, [acceptedFiles]);

  useEffect(() => {
    if (fileRejections.length > 0)
      props.onFileRejected();
  }, [fileRejections]);

  return (
    <>
      <DropzoneContainer {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
        <input {...getInputProps()} />
        <Typography align="center" variant="subtitle2">
          <p>Drag 'n' drop your CliftonStrengths 34 report here, or click to select file</p>
        </Typography>
        <CloudUploadIcon />
      </DropzoneContainer>
      {props.selectedFilePath &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {props.selectedFilePath} ({(props.selectedFileSize / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
      {fileRejections.length > 0 &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {fileRejections[0].file.name} ({(fileRejections[0].file.size / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
    </>
  )
}

const UnconnectedStep1Content = (props) => {
  const continued = useBoolean(false);

  const onFileAccepted = (file) => {
    props.validateReport(props.visitorId, file);
  }

  const onFileRejected = () => {
    props.invalidFileSelected();
  }

  const getReportValidationStatus = () => {
    props.getReportValidationStatus(props.reportId);
  }

  const handleContinue = () => {
    continued.setTrue();
    props.createOrder(props.visitorId, props.reportId);
  }

  useEffect(() => {
    if (props.orderId && continued.value) {
      props.handleNextStep();
    }
  }, [props.orderId, continued]);

  useEffect(() => {
    if (props.reportId && props.isValidating) {
      SetInterval.start(getReportValidationStatus, 2000, "pollValidationStatus");
    } else {
      SetInterval.clear("pollValidationStatus")
    }
  }, [props]);

  return (
    <>
      <StyledDropzone
        selectedFilePath={props.selectedFilePath}
        selectedFileSize={props.selectedFileSize}
        onFileAccepted={onFileAccepted}
        onFileRejected={onFileRejected}
      />
      {(props.isValidating) &&
        <>
          <Typography variant="caption">Validating</Typography>&nbsp;
          <CircularProgress color="primary" size={16} />
        </>
      }
      {props.isError &&
        <Grid container direction="row" alignItems="center" justifyContent="center" spacing="2px">
          <Grid item>
            <ErrorIcon color="error" fontSize="small" />
          </Grid>
          <Grid item>
            <Typography variant="caption" color="error">{props.errorMessage}</Typography>
          </Grid>
        </Grid>
      }
      <Typography variant="caption">
        <p>Don't have one yet? Take the CliftonStrengths assessment&nbsp;
          <Link
            href="https://store.gallup.com/p/en-sg/10003/cliftonstrengths-34"
            target="_blank"
            rel="noopener"
            underline="hover"
          >
            here
          </Link>.
        </p>
        <p>
          Be sure to purchase the <Emphasize>CliftonStrengths 34</Emphasize> report, for which Gallup normally charges S$90.&nbsp;
          <Emphasize>Note: no other Gallup CliftonStrengths reports will be accepted.</Emphasize>
        </p>
      </Typography>
      <div>
        <Button
          variant="contained"
          disabled={!props.reportId || !props.isValid}
          onClick={handleContinue}
          sx={{ mt: 1, mr: 1 }}
        >
          Continue
        </Button>
        <Button
          variant="outlined"
          disabled={true}
          onClick={props.handleBackStep}
          sx={{ mt: 1, mr: 1 }}
        >
          Back
        </Button>
      </div>
    </>
  )
}

const mapState = (state) => {
  const {
    selectedFilePath,
    selectedFileSize,
    reportId,
    isValidating,
    isValid,
    isCreatingOrder,
    orderId,
    isError,
    errorMessage,
  } = state.cs;
  const { id } = state.visitor;
  return {
    selectedFilePath,
    selectedFileSize,
    reportId,
    isValidating,
    isValid,
    isCreatingOrder,
    orderId,
    isError,
    errorMessage,
    visitorId: id,
  };
};

const actionCreators = {
  invalidFileSelected: csActions.invalidFileSelected,
  validateReport: csActions.validateReport,
  getReportValidationStatus: csActions.getReportValidationStatus,
  createOrder: csActions.createOrder,
};

export const Step1Content = connect(mapState, actionCreators)(UnconnectedStep1Content);